import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Users, 
  Image as ImageIcon, 
  Wrench, 
  MapPin, 
  FileText, 
  Share2,
  Star,
  Clock,
  ArrowRight,
  Zap,
  Filter
} from 'lucide-react'
import { SearchResult, SearchFilters } from '@/hooks/useGlobalSearch'
import { useAccessibleMotion } from '@/hooks/useAccessibleMotion'

interface SearchResultsProps {
  results: SearchResult[]
  isSearching: boolean
  query: string
  totalResults: number
  filters: SearchFilters
  onResultClick: (result: SearchResult) => void
  onFiltersChange: (filters: Partial<SearchFilters>) => void
  className?: string
}

// Icon mapping for different content types
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'lead': return Users
    case 'gallery': return ImageIcon
    case 'service': return Wrench
    case 'location': return MapPin
    case 'blog': return FileText
    case 'social_post': return Share2
    case 'page': return FileText
    case 'review': return Star
    default: return FileText
  }
}

// Color mapping for different types
const getTypeColor = (type: string) => {
  switch (type) {
    case 'lead': return 'bg-blue-500/10 text-blue-600 border-blue-200'
    case 'gallery': return 'bg-purple-500/10 text-purple-600 border-purple-200'
    case 'service': return 'bg-green-500/10 text-green-600 border-green-200'
    case 'location': return 'bg-orange-500/10 text-orange-600 border-orange-200'
    case 'blog': return 'bg-indigo-500/10 text-indigo-600 border-indigo-200'
    case 'social_post': return 'bg-pink-500/10 text-pink-600 border-pink-200'
    case 'page': return 'bg-gray-500/10 text-gray-600 border-gray-200'
    case 'review': return 'bg-yellow-500/10 text-yellow-600 border-yellow-200'
    default: return 'bg-gray-500/10 text-gray-600 border-gray-200'
  }
}

// Status color mapping
const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'published':
    case 'approved':
    case 'active':
      return 'bg-green-500/10 text-green-600 border-green-200'
    case 'draft':
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-200'
    case 'rejected':
    case 'failed':
      return 'bg-red-500/10 text-red-600 border-red-200'
    case 'scheduled':
      return 'bg-blue-500/10 text-blue-600 border-blue-200'
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-200'
  }
}

const SearchResultItem: React.FC<{
  result: SearchResult
  onClick: () => void
  index: number
}> = ({ result, onClick, index }) => {
  const { prefersReducedMotion } = useAccessibleMotion()
  const TypeIcon = getTypeIcon(result.type)

  return (
    <motion.div
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: prefersReducedMotion ? 0.01 : 0.2, 
        delay: prefersReducedMotion ? 0 : index * 0.05 
      }}
      className="group"
    >
      <Button
        variant="ghost"
        className="w-full h-auto p-4 justify-start text-left hover:bg-muted/50 transition-colors"
        onClick={onClick}
      >
        <div className="flex items-start gap-3 w-full">
          {/* Type Icon */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${getTypeColor(result.type)}`}>
            <TypeIcon className="w-4 h-4" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title and Status */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                {result.title}
              </h3>
              {result.status && (
                <Badge 
                  variant="outline" 
                  className={`text-xs px-2 py-0.5 ${getStatusColor(result.status)}`}
                >
                  {result.status}
                </Badge>
              )}
            </div>

            {/* Description */}
            {result.description && (
              <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                {result.description}
              </p>
            )}

            {/* Subtitle */}
            {result.subtitle && (
              <p className="text-xs text-muted-foreground/80">
                {result.subtitle}
              </p>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs capitalize">
                {result.type.replace('_', ' ')}
              </Badge>
              
              {result.franchise_region && result.franchise_region !== 'general' && (
                <Badge variant="outline" className="text-xs">
                  {result.franchise_region.replace('_', ' ')}
                </Badge>
              )}

              {result.created_at && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {new Date(result.created_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Arrow */}
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
        </div>
      </Button>
    </motion.div>
  )
}

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex items-start gap-3 p-4">
        <div className="w-8 h-8 bg-muted rounded-lg animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-muted rounded animate-pulse" />
          <div className="h-3 bg-muted/60 rounded animate-pulse w-2/3" />
          <div className="flex gap-2">
            <div className="h-5 bg-muted/40 rounded animate-pulse w-16" />
            <div className="h-5 bg-muted/40 rounded animate-pulse w-20" />
          </div>
        </div>
      </div>
    ))}
  </div>
)

const NoResults: React.FC<{ query: string }> = ({ query }) => (
  <div className="text-center py-8 px-4">
    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
      <Zap className="w-8 h-8 text-muted-foreground" />
    </div>
    <h3 className="text-lg font-medium mb-2">No results found</h3>
    <p className="text-sm text-muted-foreground mb-4">
      We couldn't find any results for "{query}". Try adjusting your search terms or filters.
    </p>
    <div className="space-y-2 text-xs text-muted-foreground">
      <p>Search tips:</p>
      <ul className="text-left max-w-xs mx-auto space-y-1">
        <li>• Try different keywords or synonyms</li>
        <li>• Check for typos in your search</li>
        <li>• Use fewer filters to broaden results</li>
        <li>• Search for partial matches (e.g., "brick" instead of "brickwork")</li>
      </ul>
    </div>
  </div>
)

const ContentTypeFilters: React.FC<{
  activeTypes: string[]
  onChange: (types: string[]) => void
}> = ({ activeTypes, onChange }) => {
  const contentTypes = [
    { id: 'lead', label: 'Leads', icon: Users },
    { id: 'gallery', label: 'Galleries', icon: ImageIcon },
    { id: 'service', label: 'Services', icon: Wrench },
    { id: 'location', label: 'Locations', icon: MapPin },
    { id: 'blog', label: 'Blog Posts', icon: FileText },
    { id: 'social_post', label: 'Social Posts', icon: Share2 }
  ]

  const toggleType = (typeId: string) => {
    if (activeTypes.includes(typeId)) {
      onChange(activeTypes.filter(t => t !== typeId))
    } else {
      onChange([...activeTypes, typeId])
    }
  }

  return (
    <div className="border-b border-border pb-3 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">Content Types</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {contentTypes.map(({ id, label, icon: Icon }) => (
          <Button
            key={id}
            variant={activeTypes.includes(id) ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => toggleType(id)}
          >
            <Icon className="w-3 h-3 mr-1" />
            {label}
          </Button>
        ))}
      </div>
    </div>
  )
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isSearching,
  query,
  totalResults,
  filters,
  onResultClick,
  onFiltersChange,
  className = ''
}) => {
  const { prefersReducedMotion } = useAccessibleMotion()

  return (
    <div className={`bg-card border border-border rounded-lg shadow-lg ${className}`}>
      {/* Filters */}
      <div className="p-4 border-b border-border">
        <ContentTypeFilters
          activeTypes={filters.contentTypes}
          onChange={(contentTypes) => onFiltersChange({ contentTypes })}
        />
        
        {/* Results count */}
        {!isSearching && query && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {totalResults === 0 
                ? 'No results' 
                : `${totalResults} result${totalResults === 1 ? '' : 's'}`
              } for "{query}"
            </span>
            {totalResults > 0 && (
              <span>{results.length} shown</span>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      <div className="max-h-96 overflow-y-auto">
        <AnimatePresence mode="wait">
          {isSearching ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            >
              <LoadingSkeleton />
            </motion.div>
          ) : results.length === 0 && query ? (
            <motion.div
              key="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
            >
              <NoResults query={query} />
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
              className="divide-y divide-border"
            >
              {results.map((result, index) => (
                <SearchResultItem
                  key={`${result.type}-${result.id}`}
                  result={result}
                  onClick={() => onResultClick(result)}
                  index={index}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default SearchResults